// src/pages/Admin/Dashboard/hooks/useDashboardData.js

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import { countUsers } from "../../../../services/userServices";
import {
  countClubs,
  getClubByUser,
} from "../../../../services/clubServices";
import { countDojos } from "../../../../services/dojoServices";
import { countGroups } from "../../../../services/groupServices";
import { countAdherents } from "../../../../services/adherentServices";

import {
  createReglement,
  deleteReglement,
  getReglements,
  getReglementUserOptions,
  getTotalAchats,
  updateReglement,
} from "../../../../services/achatsServices";

const initialUserStats = {
  total: 0,
  modeDemo: 0,
  presence: 0,
  presencePro: 0,
  presenceUltim: 0,
  presenceFree: 0,
  enseignants: 0,
};

const initialAbonnementStats = {
  total: 0,
  total_brut: 0,
  total_encaisse: 0,
  total_en_attente: 0,
  nombre_achats: 0,
  nombre_regles: 0,
  nombre_en_attente: 0,
  detail: {},
};

export default function useDashboardData() {
  const [userStats, setUserStats] =
    useState(initialUserStats);

  const [structureStats, setStructureStats] = useState({
    clubs: 0,
    dojos: 0,
    groups: 0,
    adherents: 0,
  });

  const [abonnementStats, setAbonnementStats] =
    useState(initialAbonnementStats);

  const [reglements, setReglements] = useState([]);
  const [reglementUsers, setReglementUsers] = useState([]);

  /*
   * Clé = user_achat_id
   * Valeur = club ou null lorsque l'utilisateur n'a pas de club.
   */
  const [clubsByUser, setClubsByUser] = useState({});

  const [lastUpdated, setLastUpdated] = useState(null);
  const [savingReglement, setSavingReglement] =
    useState(false);

  const [loading, setLoading] = useState({
    users: true,
    structure: true,
    abonnements: true,
    reglements: true,
    reglementUsers: true,
    clubs: true,
    refreshing: true,
  });

  const setLoadingKey = useCallback((key, value) => {
    setLoading((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const fetchUserStats = useCallback(async () => {
    setLoadingKey("users", true);

    try {
      const response = await countUsers();

      setUserStats({
        total: response?.total || 0,
        modeDemo: response?.modeDemo || 0,
        presence: response?.presence || 0,
        presencePro: response?.presencePro || 0,
        presenceUltim: response?.presenceUltim || 0,
        presenceFree: response?.presenceFree || 0,
        enseignants: response?.enseignants || 0,
      });
    } catch (error) {
      console.error(
        "❌ Erreur stats utilisateurs :",
        error,
      );
    } finally {
      setLoadingKey("users", false);
    }
  }, [setLoadingKey]);

  const fetchStructureStats = useCallback(async () => {
    setLoadingKey("structure", true);

    try {
      const [
        clubsResponse,
        dojosResponse,
        groupsResponse,
        adherentsResponse,
      ] = await Promise.all([
        countClubs(),
        countDojos(),
        countGroups(),
        countAdherents(),
      ]);

      setStructureStats({
        clubs: clubsResponse?.totalClubs || 0,
        dojos: dojosResponse?.totalDojos || 0,
        groups: groupsResponse?.totalGroups || 0,
        adherents:
          adherentsResponse?.totalAdherents || 0,
      });
    } catch (error) {
      console.error(
        "❌ Erreur stats structure :",
        error,
      );
    } finally {
      setLoadingKey("structure", false);
    }
  }, [setLoadingKey]);

  const fetchClubsForReglements = useCallback(
    async (reglementsList = []) => {
      setLoadingKey("clubs", true);

      try {
        const userIds = Array.from(
          new Set(
            reglementsList
              .map((reglement) =>
                Number(reglement.user_achat_id),
              )
              .filter(
                (userId) =>
                  Number.isInteger(userId) &&
                  userId > 0,
              ),
          ),
        );

        if (userIds.length === 0) {
          setClubsByUser({});
          return;
        }

        const clubEntries = await Promise.all(
          userIds.map(async (userId) => {
            try {
              const club = await getClubByUser(userId);

              return [String(userId), club || null];
            } catch (error) {
              console.error(
                `❌ Club non chargé pour l'utilisateur ${userId} :`,
                error,
              );

              return [String(userId), null];
            }
          }),
        );

        setClubsByUser((current) => ({
          ...current,
          ...Object.fromEntries(clubEntries),
        }));
      } finally {
        setLoadingKey("clubs", false);
      }
    },
    [setLoadingKey],
  );

  /*
   * Utilisé par la modale lorsqu'un administrateur sélectionne
   * un utilisateur qui ne figurait pas encore dans les achats.
   */
  const loadClubForUser = useCallback(async (userId) => {
    const parsedUserId = Number(userId);

    if (
      !Number.isInteger(parsedUserId) ||
      parsedUserId <= 0
    ) {
      return null;
    }

    try {
      const club = await getClubByUser(parsedUserId);

      setClubsByUser((current) => ({
        ...current,
        [String(parsedUserId)]: club || null,
      }));

      return club || null;
    } catch (error) {
      setClubsByUser((current) => ({
        ...current,
        [String(parsedUserId)]: null,
      }));

      throw error;
    }
  }, []);

  const fetchPurchaseData = useCallback(async () => {
    setLoadingKey("abonnements", true);
    setLoadingKey("reglements", true);

    try {
      const [statsResponse, reglementsResponse] =
        await Promise.all([
          getTotalAchats(),
          getReglements(),
        ]);

      const reglementsList = Array.isArray(
        reglementsResponse,
      )
        ? reglementsResponse
        : [];

      setAbonnementStats({
        ...initialAbonnementStats,
        ...(statsResponse || {}),
      });

      setReglements(reglementsList);

      await fetchClubsForReglements(reglementsList);
    } catch (error) {
      console.error("❌ Erreur achats :", error);
      throw error;
    } finally {
      setLoadingKey("abonnements", false);
      setLoadingKey("reglements", false);
    }
  }, [
    fetchClubsForReglements,
    setLoadingKey,
  ]);

  const fetchReglementUsers = useCallback(async () => {
    setLoadingKey("reglementUsers", true);

    try {
      const response = await getReglementUserOptions();

      setReglementUsers(
        Array.isArray(response) ? response : [],
      );
    } catch (error) {
      console.error(
        "❌ Erreur utilisateurs achats :",
        error,
      );
    } finally {
      setLoadingKey("reglementUsers", false);
    }
  }, [setLoadingKey]);

  const refreshAll = useCallback(async () => {
    setLoadingKey("refreshing", true);

    await Promise.allSettled([
      fetchUserStats(),
      fetchStructureStats(),
      fetchPurchaseData(),
      fetchReglementUsers(),
    ]);

    setLastUpdated(new Date());
    setLoadingKey("refreshing", false);
  }, [
    fetchPurchaseData,
    fetchReglementUsers,
    fetchStructureStats,
    fetchUserStats,
    setLoadingKey,
  ]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const createPurchase = useCallback(
    async (payload) => {
      setSavingReglement(true);

      try {
        const response = await createReglement(payload);

        await fetchPurchaseData();
        setLastUpdated(new Date());

        return response;
      } finally {
        setSavingReglement(false);
      }
    },
    [fetchPurchaseData],
  );

  const updatePurchase = useCallback(
    async (id, payload) => {
      setSavingReglement(true);

      try {
        const response = await updateReglement(
          id,
          payload,
        );

        await fetchPurchaseData();
        setLastUpdated(new Date());

        return response;
      } finally {
        setSavingReglement(false);
      }
    },
    [fetchPurchaseData],
  );

  const removePurchase = useCallback(
    async (id) => {
      setSavingReglement(true);

      try {
        const response = await deleteReglement(id);

        await fetchPurchaseData();
        setLastUpdated(new Date());

        return response;
      } finally {
        setSavingReglement(false);
      }
    },
    [fetchPurchaseData],
  );

  return {
    userStats,
    structureStats,
    abonnementStats,
    reglements,
    reglementUsers,
    clubsByUser,

    loading,
    savingReglement,
    lastUpdated,

    refreshAll,
    createPurchase,
    updatePurchase,
    removePurchase,
    loadClubForUser,
  };
}